// src/pages/masterdata/fosegment/FOSegmentList.jsx
import React, { useState, useEffect } from 'react';
import { Eye, Edit2, Trash2, FileText, ArrowLeft, Filter, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import DataTable from '../../../components/common/DataTable';
import FOSegmentForm from './FOSegmentForm';
import foSegmentService from '../../../services/foSegmentService';

const FOSegmentList = () => {
  const [view, setView] = useState('list'); // 'list', 'edit', 'detail'
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [segments, setSegments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Pagination & Sorting State
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [sortConfig, setSortConfig] = useState({ sortBy: 'spanId', direction: 'ASC' });
  const [searchValue, setSearchValue] = useState('');

  // Filter State
  const [filterMainArea, setFilterMainArea] = useState('ALL');
  const [filterRingType, setFilterRingType] = useState('ALL');
  const [showFilters, setShowFilters] = useState(false);

  // Statistics
  const [statistics, setStatistics] = useState(null);

  // Fetch segments on mount and when filters change
  useEffect(() => {
    fetchSegments();
    fetchStatistics();
  }, [currentPage, pageSize, sortConfig, searchValue, filterMainArea, filterRingType]);

  // Fetch all segments
  const fetchSegments = async () => {
    setIsLoading(true);
    try {
      const filters = {
        mainArea: filterMainArea !== 'ALL' ? filterMainArea : undefined,
        ringType: filterRingType !== 'ALL' ? filterRingType : undefined,
        siteName: searchValue || undefined
      };

      const sort = `${sortConfig.sortBy},${sortConfig.direction.toLowerCase()}`;
      const data = await foSegmentService.searchFOSegments(filters, currentPage, pageSize);
      
      setSegments(data.content || []);
      setTotalElements(data.totalElements || 0);
    } catch (error) {
      console.error('Error fetching segments:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch statistics
  const fetchStatistics = async () => {
    try {
      const stats = await foSegmentService.getFOSegmentStatistics();
      setStatistics(stats);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  // Define columns for DataTable
  const columns = [
    {
      key: 'no',
      label: 'No',
      sortable: false,
      width: 'w-16',
      render: (row, index) => (
        <span className="font-medium text-gray-900">
          {currentPage * pageSize + index + 1}
        </span>
      )
    },
    {
      key: 'spanId',
      label: 'Span ID',
      sortable: true,
      render: (row) => (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
          {row.spanId}
        </span>
      )
    },
    {
      key: 'siteNameAB',
      label: 'Site A & B',
      sortable: true,
      nowrap: false,
      render: (row) => (
        <div className="max-w-xs">
          <div className="font-medium text-gray-900 truncate">{row.siteNameAB}</div>
          {row.segmentId && (
            <div className="text-xs text-gray-500 mt-1">Segment: {row.segmentId}</div>
          )}
        </div>
      )
    },
    {
      key: 'mainArea',
      label: 'Main Area',
      sortable: true,
      render: (row) => (
        <div>
          <div className="text-sm font-medium text-gray-900">{row.mainArea}</div>
          {row.kota && <div className="text-xs text-gray-500">{row.kota}</div>}
        </div>
      )
    },
    {
      key: 'commercialDistance',
      label: 'Distance',
      sortable: true,
      render: (row) => (
        <div>
          <div className="text-sm font-semibold text-gray-900">
            {foSegmentService.formatDistanceKm(row.commercialDistance)}
          </div>
          <div className="text-xs text-gray-500">{row.commercialDistance} m</div>
        </div>
      )
    },
    {
      key: 'ringType',
      label: 'Ring Type',
      sortable: true,
      render: (row) => (
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
          foSegmentService.getRingTypeBadgeClass(row.ringType)
        }`}>
          {row.ringType}
        </span>
      )
    },
    {
      key: 'status',
      label: 'Status',
      sortable: false,
      render: (row) => (
        <div>
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
            foSegmentService.getStatusBadgeClass(row.status)
          }`}>
            {foSegmentService.getStatusDisplayName(row.status)}
          </span>
          {row.progressPercentage !== null && (
            <div className="mt-1">
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                  <div 
                    className="bg-blue-600 h-1.5 rounded-full transition-all"
                    style={{ width: `${row.progressPercentage}%` }}
                  ></div>
                </div>
                <span className="text-xs font-medium text-gray-600">{row.progressPercentage}%</span>
              </div>
            </div>
          )}
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (row) => (
        <div className="flex gap-2">
          <button
            onClick={() => handleView(row)}
            className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded-lg transition-colors"
            title="View Details"
          >
            <Eye className="w-5 h-5" />
          </button>
          <button
            onClick={() => handleEdit(row)}
            className="text-green-600 hover:text-green-800 p-2 hover:bg-green-50 rounded-lg transition-colors"
            title="Edit"
          >
            <Edit2 className="w-5 h-5" />
          </button>
          <button
            onClick={() => handleDelete(row)}
            className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      )
    }
  ];

  // Handlers
  const handleView = async (segment) => {
    try {
      const segmentDetail = await foSegmentService.getFOSegmentById(segment.id);
      setSelectedSegment(segmentDetail);
      setView('detail');
    } catch (error) {
      console.error('Error fetching segment details:', error);
      toast.error('Failed to load segment details');
    }
  };

  const handleEdit = async (segment) => {
    try {
      const segmentDetail = await foSegmentService.getFOSegmentById(segment.id);
      setSelectedSegment(segmentDetail);
      setView('edit');
    } catch (error) {
      console.error('Error fetching segment details:', error);
      toast.error('Failed to load segment details');
    }
  };

  const handleDelete = async (segment) => {
    if (window.confirm(`Are you sure you want to delete segment ${segment.spanId}?`)) {
      try {
        await foSegmentService.softDeleteFOSegment(segment.id);
        toast.success('Segment deleted successfully!');
        fetchSegments();
        fetchStatistics();
      } catch (error) {
        console.error('Error deleting segment:', error);
      }
    }
  };

  const handleBackToList = () => {
    setView('list');
    setSelectedSegment(null);
    fetchSegments();
    fetchStatistics();
  };

  const handleSort = (newSortConfig) => {
    setSortConfig(newSortConfig);
    setCurrentPage(0);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(0);
  };

  const handleSearch = (value) => {
    setSearchValue(value);
    setCurrentPage(0);
  };

  const clearFilters = () => {
    setFilterMainArea('ALL');
    setFilterRingType('ALL');
    setSearchValue('');
    setCurrentPage(0);
  };

  // Render Form View (Edit Only)
  if (view === 'edit') {
    return (
      <FOSegmentForm
        onBack={handleBackToList}
        segmentData={selectedSegment}
        mode="edit"
      />
    );
  }

  // Render Detail View
  if (view === 'detail' && selectedSegment) {
    return (
      <div className="space-y-6">
        {/* Page Header */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBackToList}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">FO Segment Details</h1>
                <p className="text-gray-600 mt-1">View segment information</p>
              </div>
            </div>
            <button
              onClick={() => handleEdit(selectedSegment)}
              className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-3 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center gap-2"
            >
              <Edit2 className="w-5 h-5" />
              Edit Segment
            </button>
          </div>
        </div>

        {/* Detail Card */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200">
          <div className="p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4">Basic Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold text-gray-500 mb-2">Span ID</label>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-blue-100 text-blue-800">
                  {selectedSegment.spanId}
                </span>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold text-gray-500 mb-2">Ring Type</label>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                  foSegmentService.getRingTypeBadgeClass(selectedSegment.ringType)
                }`}>
                  {selectedSegment.ringType}
                </span>
              </div>
              <div className="md:col-span-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold text-gray-500 mb-2">Site Name A & B</label>
                <p className="text-base text-gray-900">{selectedSegment.siteNameAB}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold text-gray-500 mb-2">Main Area</label>
                <p className="text-base text-gray-900">{selectedSegment.mainArea}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold text-gray-500 mb-2">Kota</label>
                <p className="text-base text-gray-900">{selectedSegment.kota}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold text-gray-500 mb-2">Commercial Distance</label>
                <p className="text-lg font-semibold text-gray-900">
                  {foSegmentService.formatDistanceKm(selectedSegment.commercialDistance)}
                </p>
                <p className="text-sm text-gray-500">{selectedSegment.commercialDistance} meters</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold text-gray-500 mb-2">Status</label>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                  foSegmentService.getStatusBadgeClass(selectedSegment.status)
                }`}>
                  {foSegmentService.getStatusDisplayName(selectedSegment.status)}
                </span>
              </div>
            </div>
          </div>

          {/* Project Info */}
          {selectedSegment.projectCode && (
            <div className="border-t border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4">Project Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <label className="block text-sm font-semibold text-gray-500 mb-2">Project Code</label>
                  <p className="text-base text-gray-900">{selectedSegment.projectCode}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <label className="block text-sm font-semibold text-gray-500 mb-2">SPK Number</label>
                  <p className="text-base text-gray-900">{selectedSegment.spkNumber}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Render List View (Default)
  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg shadow-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Total Segments</p>
                <p className="text-3xl font-bold mt-2">{statistics.totalSegments}</p>
              </div>
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-red-600 to-red-500 rounded-lg shadow-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm font-medium">Total Distance</p>
                <p className="text-3xl font-bold mt-2">{statistics.totalCommercialDistanceKm?.toFixed(2)} km</p>
                <p className="text-red-100 text-xs mt-1">{statistics.totalCommercialDistance?.toLocaleString()} meters</p>
              </div>
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-600 to-green-500 rounded-lg shadow-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">Avg Distance</p>
                <p className="text-3xl font-bold mt-2">{statistics.averageCommercialDistance?.toFixed(0)} m</p>
              </div>
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Page Header with Filters */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center shadow-md">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">FO Segment List</h1>
                <p className="text-gray-600 mt-1">Manage fiber optic segments</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 font-medium px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
              >
                <Filter className="w-4 h-4" />
                Filters
              </button>
              <button
                className="border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export
              </button>
            </div>
          </div>

          {/* Filter Section */}
          {showFilters && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Main Area</label>
                  <select
                    value={filterMainArea}
                    onChange={(e) => {
                      setFilterMainArea(e.target.value);
                      setCurrentPage(0);
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="ALL">All Main Areas</option>
                    <option value="Jawa Tengah">Jawa Tengah</option>
                    <option value="Jawa Timur">Jawa Timur</option>
                    <option value="Jawa Barat">Jawa Barat</option>
                    <option value="DKI Jakarta">DKI Jakarta</option>
                    <option value="Banten">Banten</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Ring Type</label>
                  <select
                    value={filterRingType}
                    onChange={(e) => {
                      setFilterRingType(e.target.value);
                      setCurrentPage(0);
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="ALL">All Ring Types</option>
                    <option value="Insert">Insert</option>
                    <option value="Add Span">Add Span</option>
                    <option value="Star">Star</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={clearFilters}
                    className="w-full border border-gray-300 text-gray-700 hover:bg-gray-100 font-medium px-4 py-2 rounded-lg transition-colors"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* DataTable Component */}
        <DataTable
          columns={columns}
          data={segments}
          currentPage={currentPage}
          pageSize={pageSize}
          totalElements={totalElements}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSort={handleSort}
          sortConfig={sortConfig}
          isLoading={isLoading}
          onSearch={handleSearch}
          searchValue={searchValue}
          searchPlaceholder="Search by span ID, site name..."
          enableQuickJump={true}
          customPageSizes={[10, 25, 50, 100]}
          striped={false}
          headerBgColor="bg-gradient-to-r from-red-600 to-red-500"
          headerTextColor="text-white"
          hoverColor="hover:bg-red-50"
        />
      </div>
    </div>
  );
};

export default FOSegmentList;